import { IndicatorState, SERVICE_DESCRIPTIONS, ServiceHealth } from '@/types';
import { getIndicator } from '@/services/indicators';
import { StatusIcon } from './StatusIcon';
import { RelativeTime } from './RelativeTime';
import { ServiceHistoryStrip } from './ServiceHistoryStrip';

interface ServiceRowProps {
  service: ServiceHealth;
  /** Health could not be determined — do not paint this green. */
  statusUnknown?: boolean;
}

/**
 * One service, as a ROW rather than a card.
 *
 * Vercel and GitHub both settled on rows in a single container: cards put a
 * border around every service, which makes ten healthy services look like ten
 * things demanding attention. Rows separated by hairlines let the status column
 * line up down the right edge, so scanning for the one word that isn't
 * "Operational" takes one pass of the eye instead of ten.
 */
export function ServiceRow({ service, statusUnknown }: ServiceRowProps) {
  const state: IndicatorState = statusUnknown ? 'unknown' : service.status;
  const indicator = getIndicator(state);
  const description = SERVICE_DESCRIPTIONS[service.name];

  // A deduplicated error can represent hundreds of failures, so show the
  // occurrence total whenever it exceeds the number of distinct errors. Hiding
  // that gap once made a 112-failure incident read as a single error.
  const hasRepeats = service.occurrenceCount > service.errorCount;

  return (
    <div className="px-4 py-3.5 sm:px-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-medium text-slate-900">{service.displayName}</h3>
          {description && (
            <p className="mt-0.5 truncate text-xs text-slate-500">{description}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <StatusIcon state={state} className="h-3.5 w-3.5" />
          <span className={`text-xs font-medium ${indicator.text}`}>
            {indicator.label}
          </span>
        </div>
      </div>

      <div className="mt-3">
        <ServiceHistoryStrip history={service.history} unknown={statusUnknown} />
      </div>

      {!statusUnknown && (service.errorCount > 0 || service.lastError) && (
        <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
          {service.errorCount > 0 && (
            <span>
              {service.errorCount} error{service.errorCount !== 1 ? 's' : ''} in last hour
              {hasRepeats && (
                <> · {service.occurrenceCount.toLocaleString()} occurrences</>
              )}
            </span>
          )}
          {service.lastError && (
            <>
              {service.errorCount > 0 && <span aria-hidden>·</span>}
              <RelativeTime date={service.lastError} prefix="Last error" />
            </>
          )}
        </p>
      )}
    </div>
  );
}
